// Lazy Cloudflare Turnstile loader used only after the server requests an
// adaptive challenge. The widget key is public; Siteverify remains backend-only.

import type { ChallengeConfig } from "./schema";

const SCRIPT_ID = "sanctum-forms-turnstile";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export type TurnstileWidgetId = string | number;

export interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      cData: string;
      theme: "auto";
      size: "flexible";
      "response-field": false;
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
      "timeout-callback": () => void;
    },
  ): TurnstileWidgetId;
  reset(widgetId: TurnstileWidgetId): void;
  remove?(widgetId: TurnstileWidgetId): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loader) return loader;

  loader = new Promise<TurnstileApi>((resolve, reject) => {
    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Security verification did not initialise."));
    };
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", settle, { once: true });
      existing.addEventListener("error", () => reject(new Error("Security verification could not load.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", settle, { once: true });
    script.addEventListener("error", () => reject(new Error("Security verification could not load.")), {
      once: true,
    });
    document.head.appendChild(script);
  }).catch((error) => {
    loader = null;
    throw error;
  });
  return loader;
}

export function renderTurnstile(
  api: TurnstileApi,
  container: HTMLElement,
  config: ChallengeConfig,
  callbacks: {
    success: (token: string) => void;
    error: () => void;
    expired: () => void;
  },
): TurnstileWidgetId {
  return api.render(container, {
    sitekey: config.site_key,
    action: config.action,
    cData: config.cdata,
    theme: "auto",
    size: "flexible",
    "response-field": false,
    callback: callbacks.success,
    "error-callback": callbacks.error,
    "expired-callback": callbacks.expired,
    "timeout-callback": callbacks.expired,
  });
}
